"use client";

import { useEffect, useState } from "react";

import Image from "next/image";
import Link from "next/link";
import { usePathname } from "next/navigation";

import SeoCtaLink from "@/components/seo/SeoCtaLink";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import config from "@/config";
import { getSeoAwarePath } from "@/content/seo/locale-rollout";
import { getLocalizedSiteChrome } from "@/content/seo/site-chrome";
import logo from "@/public/images/logo.png";
import { getLocalizedPath } from "@/utils/utils";
import { createClientComponentClient } from "@supabase/auth-helpers-nextjs";
import {
  ChevronDown,
  LayoutDashboard,
  Menu as MenuIcon,
  X,
} from "lucide-react";

import LanguageSwitcher from "./LanguageSwitcher";

const useCaseLinks = [
  {
    href: "/faceless-youtube-video-generator",
    label: "YouTube videos",
    description: "Create Shorts and long-form channel videos.",
  },
  {
    href: "/faceless-tiktok-video-generator",
    label: "TikTok videos",
    description: "Build vertical videos with readable captions.",
  },
  {
    href: "/faceless-instagram-reels-generator",
    label: "Instagram Reels",
    description: "Create consistent presenter-free Reels.",
  },
  {
    href: "/faceless-podcast-video-generator",
    label: "Podcast videos",
    description: "Turn scripted conversations into visual episodes.",
  },
];

const defaultWorkflowLinks = [
  {
    href: "/faceless-video-automation",
    label: "Topic to faceless video",
    description: "Build a repeatable series from one focused idea.",
  },
  {
    href: "/script-to-faceless-video",
    label: "Script to faceless video",
    description: "Turn approved narration into editable scenes.",
  },
  {
    href: "/url-to-faceless-video",
    label: "URL to faceless video",
    description: "Adapt a public source into a visual draft.",
  },
];

const defaultDialogueWorkflow = {
  href: "/dashboard/create-workflow/english-dialogue",
  label: "English scene dialogue",
  description: "Create a two-person English dialogue with distinct voices.",
};

const Header = ({ locale = "en" }: { locale?: string }) => {
  const pathname = usePathname();
  const supabase = createClientComponentClient();
  const localeContent = getLocalizedSiteChrome(locale);
  const localized = localeContent?.navigation;
  const localizedUseCaseLinks = localized?.useCaseLinks || useCaseLinks;
  const [isOpen, setIsOpen] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const dialogueWorkflowPath = getLocalizedPath(
    defaultDialogueWorkflow.href,
    locale,
  );
  const dialogueWorkflowHref = isLoggedIn
    ? dialogueWorkflowPath
    : `${getLocalizedPath("/signup", locale)}?${new URLSearchParams({
        next: dialogueWorkflowPath,
        source: "header-english-dialogue",
        landingPath: pathname || "/",
        location: "navigation-workflows",
        locale,
      }).toString()}`;
  const workflowMenuLinks = [
    ...(localeContent?.workflowLinks || defaultWorkflowLinks).map((link) => ({
      ...link,
      href: getSeoAwarePath(link.href, locale),
    })),
    {
      ...(localeContent?.dialogueWorkflow || defaultDialogueWorkflow),
      href: dialogueWorkflowHref,
    },
  ];

  const links = [
    {
      href: getSeoAwarePath("/examples", locale),
      label: localeContent?.examplesNavigationLabel || "Examples",
    },
    {
      href: getSeoAwarePath("/pricing", locale),
      label: localized?.pricing || "Pricing",
    },
  ];

  useEffect(() => {
    let mounted = true;

    supabase.auth.getUser().then(({ data }) => {
      if (mounted) setIsLoggedIn(Boolean(data.user));
    });
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setIsLoggedIn(Boolean(session?.user));
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, [supabase]);

  useEffect(() => {
    setIsOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setIsOpen(false);
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen]);

  return (
    // Hallmark N11: compact production mega-menu; the grouped workflow index earns the wider panel.
    <header className="fixed inset-x-0 top-0 z-[var(--z-nav)] border-b border-[var(--color-rule)] bg-[var(--color-paper)] text-[var(--color-ink)] [font-family:var(--font-body)]">
      <nav
        aria-label="Global"
        className="mx-auto flex h-16 max-w-[var(--page-max)] items-center justify-between px-4 sm:px-8 lg:px-10"
      >
        <Link
          href={getLocalizedPath("/", locale)}
          className="flex min-h-11 shrink-0 items-center gap-2.5 rounded-[var(--radius-small)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[var(--color-focus)]"
        >
          <Image
            src={logo}
            alt={`${config.appName} logo`}
            width={32}
            height={32}
            priority
            className="rounded-[var(--radius-small)] ring-1 ring-[var(--color-rule)]"
          />
          <span className="text-lg font-bold tracking-[-0.025em] text-[var(--color-ink)]">AriaFlow.ai</span>
        </Link>

        <div className="hidden items-center gap-6 lg:flex">
          <DropdownMenu>
            <DropdownMenuTrigger className="group flex min-h-11 items-center gap-1 whitespace-nowrap rounded-[var(--radius-small)] px-1 text-sm font-semibold text-[var(--color-muted)] outline-none transition-colors duration-150 hover:text-[var(--color-accent-deep)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[var(--color-focus)] active:text-[var(--color-ink)]">
              {localeContent?.workflowLabel || "Workflows"}
              <ChevronDown
                aria-hidden="true"
                className="h-4 w-4 transition-transform duration-200 group-data-[state=open]:rotate-180 motion-reduce:transition-none"
              />
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="start"
              sideOffset={10}
              className="grid w-[42rem] max-w-[var(--radix-dropdown-menu-content-available-width)] grid-cols-2 gap-1 rounded-[var(--radius-card)] border-[var(--color-rule)] bg-[var(--color-paper-raised)] p-2 text-[var(--color-ink)] shadow-[var(--shadow-menu)]"
            >
              {workflowMenuLinks.map((link) => (
                <DropdownMenuItem
                  key={link.href}
                  asChild
                  className="cursor-pointer flex-col items-start rounded-[var(--radius-small)] p-0 focus:bg-[var(--color-accent-soft)] focus:text-[var(--color-ink)]"
                >
                  <Link
                    href={link.href}
                    className="block w-full px-3 py-3 outline-none"
                  >
                    <span className="block text-sm font-semibold">
                      {link.label}
                    </span>
                    {link.description && (
                      <span className="mt-1 block text-xs leading-5 text-[var(--color-muted)]">
                        {link.description}
                      </span>
                    )}
                  </Link>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          <DropdownMenu>
            <DropdownMenuTrigger className="group flex min-h-11 items-center gap-1 whitespace-nowrap rounded-[var(--radius-small)] px-1 text-sm font-semibold text-[var(--color-muted)] outline-none transition-colors duration-150 hover:text-[var(--color-accent-deep)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[var(--color-focus)] active:text-[var(--color-ink)]">
              {localized?.useCases || "Use Cases"}
              <ChevronDown
                aria-hidden="true"
                className="h-4 w-4 transition-transform duration-200 group-data-[state=open]:rotate-180 motion-reduce:transition-none"
              />
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="center"
              sideOffset={14}
              className="grid w-[42rem] max-w-[var(--radix-dropdown-menu-content-available-width)] grid-cols-2 gap-1 rounded-[var(--radius-card)] border-[var(--color-rule)] bg-[var(--color-paper-raised)] p-2 text-[var(--color-ink)] shadow-[var(--shadow-menu)]"
            >
              {localizedUseCaseLinks.map((link) => (
                <DropdownMenuItem
                  key={link.href}
                  asChild
                  className="cursor-pointer flex-col items-start rounded-[var(--radius-small)] p-0 focus:bg-[var(--color-accent-soft)] focus:text-[var(--color-ink)]"
                >
                  <Link
                    href={getSeoAwarePath(link.href, locale)}
                    className="block w-full px-3 py-3 outline-none"
                  >
                    <span className="block text-sm font-semibold">
                      {link.label}
                    </span>
                    <span className="mt-1 block text-xs leading-5 text-[var(--color-muted)]">
                      {link.description}
                    </span>
                  </Link>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {links.map((link) => (
            <Link
              key={link.href}
              href={link.href}
              className="inline-flex min-h-11 items-center whitespace-nowrap rounded-[var(--radius-small)] px-1 text-sm font-semibold text-[var(--color-muted)] transition-colors duration-150 hover:text-[var(--color-accent-deep)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[var(--color-focus)] active:text-[var(--color-ink)]"
            >
              {link.label}
            </Link>
          ))}
        </div>

        <div className="hidden items-center gap-3 lg:flex">
          <LanguageSwitcher locale={locale} />
          {isLoggedIn ? (
            <a
              href={getLocalizedPath("/dashboard", locale)}
              className="inline-flex min-h-11 items-center justify-center whitespace-nowrap rounded-[var(--radius-input)] bg-[var(--color-accent)] px-4 py-2 text-sm font-semibold text-[var(--color-accent-ink)] transition-[background-color,transform] duration-150 hover:bg-[var(--color-accent-deep)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-focus)] active:translate-y-px"
            >
              {localized?.dashboard || "Dashboard"}
            </a>
          ) : (
            <>
              <a
                href={getLocalizedPath("/login", locale)}
                className="inline-flex min-h-11 items-center whitespace-nowrap rounded-[var(--radius-small)] px-1 text-sm font-semibold text-[var(--color-ink-2)] transition-colors duration-150 hover:text-[var(--color-accent-deep)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[var(--color-focus)] active:text-[var(--color-ink)]"
              >
                {localized?.signIn || "Sign in"}
              </a>
              <SeoCtaLink
                locale={locale}
                source="header"
                location="navigation"
                className="inline-flex min-h-11 items-center justify-center whitespace-nowrap rounded-[var(--radius-input)] bg-[var(--color-accent)] px-4 py-2 text-sm font-semibold text-[var(--color-accent-ink)] transition-[background-color,transform] duration-150 hover:bg-[var(--color-accent-deep)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-focus)] active:translate-y-px"
              >
                {localized?.startFree || "Start free"}
              </SeoCtaLink>
            </>
          )}
        </div>

        <div className="flex items-center gap-2 lg:hidden">
          {!isLoggedIn && (
            <Link
              href={getLocalizedPath("/login", locale)}
              className="inline-flex min-h-11 items-center justify-center whitespace-nowrap rounded-[var(--radius-input)] border border-[var(--color-rule-2)] bg-[var(--color-paper)] px-3 py-2 text-sm font-semibold text-[var(--color-ink)] transition-colors duration-150 hover:bg-[var(--color-accent-soft)] hover:text-[var(--color-accent-deep)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-focus)] active:bg-[var(--color-paper-2)]"
            >
              {localized?.signIn || "Sign in"}
            </Link>
          )}
          <button
            type="button"
            aria-label={
              isOpen
                ? localized?.close || "Close"
                : localized?.menu || "Menu"
            }
            aria-expanded={isOpen}
            aria-controls="mobile-navigation"
            onClick={() => setIsOpen((open) => !open)}
            className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-[var(--radius-input)] border border-[var(--color-rule-2)] bg-[var(--color-paper)] text-[var(--color-ink)] transition-colors duration-150 hover:bg-[var(--color-accent-soft)] hover:text-[var(--color-accent-deep)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-focus)] active:bg-[var(--color-paper-2)]"
          >
            {isOpen ? (
              <X className="h-5 w-5" aria-hidden="true" />
            ) : (
              <MenuIcon className="h-5 w-5" aria-hidden="true" />
            )}
          </button>
        </div>
      </nav>

      {isOpen && (
        <div
          id="mobile-navigation"
          className="max-h-[calc(100dvh-var(--header-height))] overflow-y-auto overscroll-contain border-t border-[var(--color-rule)] bg-[var(--color-paper)] px-4 py-5 lg:hidden"
        >
          <div className="mx-auto max-w-[var(--page-max)]">
            {isLoggedIn ? (
              <Link
                href={getLocalizedPath("/dashboard", locale)}
                className="flex min-h-11 w-full items-center justify-center gap-3 whitespace-nowrap rounded-[var(--radius-input)] bg-[var(--color-accent)] px-4 py-2 text-sm font-semibold text-[var(--color-accent-ink)] transition-colors duration-150 hover:bg-[var(--color-accent-deep)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-focus)] active:bg-[var(--color-accent-deep)]"
              >
                <LayoutDashboard className="h-5 w-5 shrink-0" aria-hidden="true" />
                {localized?.dashboard || "Dashboard"}
              </Link>
            ) : (
              <SeoCtaLink
                locale={locale}
                source="header"
                location="mobile-navigation"
                className="inline-flex min-h-11 w-full items-center justify-center whitespace-nowrap rounded-[var(--radius-input)] bg-[var(--color-accent)] px-4 py-2 text-sm font-semibold text-[var(--color-accent-ink)] transition-colors duration-150 hover:bg-[var(--color-accent-deep)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-focus)] active:bg-[var(--color-accent-deep)]"
              >
                {localized?.startFree || "Start free"}
              </SeoCtaLink>
            )}

            <div className="mt-5 border-t border-[var(--color-rule)] pt-4">
              <p className="px-3 pb-2 text-sm font-semibold text-[var(--color-muted)]">
                {localeContent?.workflowLabel || "Workflows"}
              </p>
              <div className="grid gap-1">
                {workflowMenuLinks.map((link) => (
                  <Link
                    key={link.href}
                    href={link.href}
                    className="flex min-h-11 items-center whitespace-nowrap rounded-[var(--radius-small)] px-3 py-2 text-base font-semibold text-[var(--color-ink)] transition-colors duration-150 hover:bg-[var(--color-accent-soft)] hover:text-[var(--color-accent-deep)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-[var(--color-focus)] active:bg-[var(--color-paper-2)]"
                  >
                    {link.label}
                  </Link>
                ))}
              </div>
            </div>

            <div className="mt-4 border-t border-[var(--color-rule)] pt-4">
              <p className="px-3 pb-2 text-sm font-semibold text-[var(--color-muted)]">
                {localized?.useCases || "Use Cases"}
              </p>
              <div className="grid gap-1">
                {localizedUseCaseLinks.map((link) => (
                  <Link
                    key={link.href}
                    href={getSeoAwarePath(link.href, locale)}
                    className="flex min-h-11 items-center whitespace-nowrap rounded-[var(--radius-small)] px-3 py-2 text-base font-semibold text-[var(--color-ink)] transition-colors duration-150 hover:bg-[var(--color-accent-soft)] hover:text-[var(--color-accent-deep)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-[var(--color-focus)] active:bg-[var(--color-paper-2)]"
                  >
                    {link.label}
                  </Link>
                ))}
              </div>
            </div>

            <div className="mt-4 grid gap-1 border-t border-[var(--color-rule)] pt-4">
              {links.map((link) => (
                <Link
                  key={link.href}
                  href={link.href}
                  className="flex min-h-11 items-center whitespace-nowrap rounded-[var(--radius-small)] px-3 py-2 text-base font-semibold text-[var(--color-ink)] transition-colors duration-150 hover:bg-[var(--color-accent-soft)] hover:text-[var(--color-accent-deep)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-[var(--color-focus)] active:bg-[var(--color-paper-2)]"
                >
                  {link.label}
                </Link>
              ))}
            </div>

            <div className="mt-5 border-t border-[var(--color-rule)] px-3 pt-5">
              <LanguageSwitcher locale={locale} />
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
